import { forwardRef } from "react";
import { MapPin, Clock, Navigation } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Tables } from "@/integrations/supabase/types";
import { getEffectiveProducerLocation, buildGoogleMapsUrl, type EffectiveLocation } from "@/lib/producer/getEffectiveLocation";
import { useTranslate } from "@/i18n";

type Producer = Tables<"producers">;

interface ProducerVisitProps {
  producer: Producer;
  googleMapsUrl: string;
  /** If provided, used for effective location resolution */
  linkedPlace?: { address_text?: string | null; municipality?: string | null; landskap?: string | null; lat?: number | null; lng?: number | null } | null;
}

export const ProducerVisit = forwardRef<HTMLElement, ProducerVisitProps>(
  function ProducerVisit({ producer, googleMapsUrl, linkedPlace }, ref) {
    const t = useTranslate();
    const effective = getEffectiveProducerLocation(producer, linkedPlace);
    const effectiveGoogleMapsUrl = effective.lat && effective.lng
      ? buildGoogleMapsUrl(effective, producer.name)
      : googleMapsUrl;

    const hasOpeningHours = producer.opening_hours_text;
    const hasAddress = effective.addressText;
    const hasMap = effective.lat && effective.lng;
    
    if (!hasOpeningHours && !hasAddress && !hasMap) return null;

    return (
      <section ref={ref} className="border-t border-border/30 pt-8">
        <div className="max-w-2xl">
          <h2 className="font-display text-lg text-foreground mb-4">
            {t("producer.visit.heading")}
          </h2>
          
          <div className="space-y-4 sm:space-y-6">
            {hasOpeningHours && (
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted text-muted-foreground shrink-0">
                  <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground text-sm sm:text-base mb-1">{t("producer.visit.openingHours")}</h3>
                  <p className="text-foreground/70 text-sm sm:text-base whitespace-pre-line">
                    {producer.opening_hours_text}
                  </p>
                </div>
              </div>
            )}
            
            {hasAddress && (
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-muted text-muted-foreground shrink-0">
                  <MapPin className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div>
                  <h3 className="font-medium text-foreground text-sm sm:text-base mb-1">{t("producer.visit.address")}</h3>
                  <p className="text-foreground/70 text-sm sm:text-base">
                    {effective.addressText}
                  </p>
                  {effective.city && (
                    <p className="text-foreground/50 text-sm">
                      {effective.city}{effective.region ? `, ${effective.region}` : ''}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {hasMap && (
            <div className="mt-6 sm:mt-8">
              <div className="aspect-[4/3] sm:aspect-[16/9] bg-muted/30 rounded-xl sm:rounded-2xl overflow-hidden border border-border/30">
                <iframe
                  title={`Karta över ${producer.name}`}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${effective.lng! - 0.02}%2C${effective.lat! - 0.01}%2C${effective.lng! + 0.02}%2C${effective.lat! + 0.01}&layer=mapnik&marker=${effective.lat}%2C${effective.lng}`}
                  className="w-full h-full"
                  loading="lazy"
                />
              </div>
              <div className="mt-3 sm:mt-4">
                <Button variant="outline" className="rounded-full text-sm" asChild>
                  <a href={effectiveGoogleMapsUrl} target="_blank" rel="noopener noreferrer">
                    <Navigation className="h-4 w-4" />
                    {t("producer.visit.openMaps")}
                  </a>
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  }
);
